<template>
  <section class="form-list">
    <el-form
      class="form form-max"
      ref="form"
      :rules="rules"
      :model="postData"
      label-position="right"
      label-width="130px"
    >
      <el-form-item label="延期门店" prop="store_ids">
        <v-select-store
          v-if="isAdministrator"
          v-model="postData.store_ids"
          :multiple="true"
          :has-private="true"
          class="form-input"
          placeholder="请选择需要延期的门店"
          @input="getCardList"
        />
      </el-form-item>

      <el-form-item label="课卡类型" prop="card_type_ids">
        <v-select-card-type-id
          v-model="postData.card_type_ids"
          :multiple="true"
          placeholder="请选择课卡类型"
          class="form-input"
          @input="getCardList"
        />
      </el-form-item>

      <el-form-item label="课卡期限类型" prop="card_limit_type">
        <el-checkbox-group v-model="cardLimitTypesChecked" :min="1">
          <el-checkbox
            v-for="limitType in cardLimitTypes"
            :key="`cardLimitType${limitType.value}`"
            :label="limitType.value"
          >
            {{ limitType.name }}（{{ limitType.tip }}）
          </el-checkbox>
        </el-checkbox-group>
      </el-form-item>

      <el-form-item label="是否延期课次券">
        <el-switch v-model="postData.is_has_card_ticket" :active-value="1" :inactive-value="0"/>
      </el-form-item>

      <el-form-item label="停课期间" prop="stop_course_dates">
        <el-date-picker
          unlink-panels
          type="daterange"
          align="right"
          value-format="yyyy-MM-dd"
          range-separator="至"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          v-model="postData.stop_course_dates"
          :pickerOptions="pickerOptionsStopCourseDelayDate"
          @change="handleStopCourseDatesChange"
        />
        <el-alert
          title="停课期间包含开始日期、结束日期（即这两天都是无课的），系统将根据您所选择的日期计算出停课总天数（即延期天数）"
          type="warning"
          show-icon
          :closable="false"
          class="form-input-tip form-input"
        />
      </el-form-item>

      <el-form-item label="延期天数">
        <span class="primary-color">{{ postData.delay_days }}</span> 天
      </el-form-item>

      <el-form-item label="延期课卡预览"></el-form-item>

      <el-table
        v-loading="isLoading"
        :data="list"
        :key="tableKey"
        highlight-current-row
        class="card-list"
      >
        <el-table-column label="课卡 ID" align="center">
          <template v-slot="{ row }">
            <span>{{ row.id }}</span>
          </template>
        </el-table-column>

        <el-table-column v-if="isAdministrator" label="办卡门店" align="center">
          <template v-slot="{ row }">
            <span>{{ row.store_name }}</span>
          </template>
        </el-table-column>

        <v-column-student label="所属学员" link-page-id="student_id"/>

        <el-table-column label="课卡类型" align="center" prop="card_type_name"/>

        <el-table-column label="课卡名称" align="center" prop="card_name"/>

        <el-table-column label="失效时间" align="center">
          <template v-slot="{ row }">
            <span>{{ row.expire_time | timeToYmdHis('未开卡') }}</span>
          </template>
        </el-table-column>

        <el-table-column label="剩余次数" align="center">
          <template v-slot="{ row }">
            <span v-if="row.is_number_limit">{{ row.expire_number | toYuan }}</span>
            <span v-else>-</span>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-box">
        <el-pagination
          v-if="count"
          :background="true"
          :current-page.sync="page"
          :page-size.sync="size"
          :page-sizes="pageSizes"
          :total="count"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handlePageChange"
          @current-change="handlePageChange"
        />
      </div>

      <el-form-item>
        <el-button :loading="isSaving" type="primary" @click="handleSubmit">立即延期</el-button>
      </el-form-item>
    </el-form>
  </section>
</template>

<script>
import RouteConfig from '@/router/config';
import { getMilliseconds, pickerOptionsStopCourseDelayDate } from '@/utils/formats';
import { getDelayedCards, updateDateBatch } from '@/api/card';
import cardLimitTypes from "@/enums/cardLimitTypes";

export default {
  name: RouteConfig.cardEditDateBatch.name,
  data: () => ({
    pickerOptionsStopCourseDelayDate,
    cardLimitTypes,
    cardLimitTypesChecked: [cardLimitTypes.number.value, cardLimitTypes.time.value],
    isLoading: false,
    isSaving: false,
    postData: {
      store_ids: [],
      card_type_ids: [],
      card_limit_type: 0,
      is_has_card_ticket: 1,
      stop_course_dates: [],
      stop_course_start_date: '',
      stop_course_end_date: '',
      delay_days: 0,
    },
    list: [],
    count: 0,
    tableKey: 0,
    page: 1,
    size: 10,
    pageSizes: [10, 20, 30],
    rules: {
      store_ids: [{ required: true, message: '请选择门店', trigger: 'change' }],
      card_type_ids: [{ required: true, message: '请选择课卡类型', trigger: 'change' }],
      stop_course_dates: [{ required: true, message: '请选择停课日期', trigger: 'change' }],
      update_remark: [{ required: true, message: '请输入修改课卡的原因', trigger: 'change' }],
    },
    pickerOptions: {
      disabledDate: (time) => time.getTime() < 0,
    },
  }),
  watch: {
    cardLimitTypesChecked(types) {
      if (types.length === 2) {
        this.postData.card_limit_type = 0;
        return this.getCardList();
      }

      for (let k in cardLimitTypes) {
        if (cardLimitTypes[k].value === types[0]) {
          this.postData.card_limit_type = types[0];
          return this.getCardList();
        }
      }
    },
  },
  computed: {
    isAdministrator() {
      return this.$store.getters.isAdministrator;
    },
  },
  methods: {
    handlePageChange() {
      this.getCardList();
    },
    calculateBetweenDays(startDate, endDate) {
      if (!startDate || !endDate) return 0;

      endDate = getMilliseconds(endDate, 'ymd');
      if (!Number.isInteger(endDate)) return 0;

      startDate = getMilliseconds(startDate, 'ymd');
      if (!Number.isInteger(startDate)) return 0;

      const timeDiff = Math.abs(endDate - startDate);
      return Math.ceil(timeDiff / 86400000) + 1;
    },
    isNotYmdString(data) {
      return !/^\d{4}-\d{2}-\d{2}$/.test(data);
    },
    setDates(dates = ['', '']) {
      Object.assign(this.postData, {
        stop_course_start_date: dates[0],
        stop_course_end_date: dates[1],
        delay_days: this.calculateBetweenDays(dates[0], dates[1]),
      });
      this.getCardList();
    },
    handleStopCourseDatesChange(dates) {
      if (!Array.isArray(dates) || dates.length !== 2) return this.setDates();

      if (this.isNotYmdString(dates[0]) || this.isNotYmdString(dates[1])) {
        this.setDates();
        return this.$message.warning('浏览器不兼容此日期选择器，请更新浏览器或更换其他浏览器进行尝试！');
      }

      this.setDates(dates);
    },
    async getCardList() {
      const { store_ids, card_type_ids, card_limit_type, stop_course_start_date } = this.postData;

      if (!store_ids.length) return;
      if (!card_type_ids.length) return;
      if (!stop_course_start_date) return;

      this.isLoading = true;
      const { isError, data } = await getDelayedCards({
        store_ids,
        card_type_ids,
        card_limit_type,
        stop_course_start_date,
        page: this.page,
        size: this.size,
      });
      this.isLoading = false;
      if (isError) return;

      this.count = data.count;
      this.list = data.list;
      this.tableKey += 1;
    },
    handleSubmit() {
      if (this.isSaving) return;

      this.$refs.form.validate(async (isSuccess) => {
        if (!isSuccess) return;

        const postData = { ...this.postData };
        this.isSaving = true;
        const { isError } = await updateDateBatch(postData);
        this.isSaving = false;
        if (isError) return;

        RouteConfig.cardUpdateRecordList.push();
      });
    },
  },
};
</script>

<style scoped>
  .card-list {
    margin-bottom: 20px;
  }

  .pagination-box {
    margin: 20px 0;
  }
</style>
